import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { ptBR } from '@/i18n/pt-BR';
import { useSessionStore } from '@/stores/session';

/**
 * Modal cooperativo de sessão expirada. Disparado pelo interceptor 401 do
 * axios (via flag `sessionExpired` no `useSessionStore`).
 *
 * "Entrar novamente" preserva a rota atual via `?next=<pathname+search>` para
 * que o LoginPage possa retornar o usuário ao mesmo ponto após autenticar.
 * "Sair" navega para `/login` sem `?next=`.
 *
 * Atenção: o AuthGuard reage à mesma redefinição da sessão e dispara
 * `<Navigate to="/login" />` em paralelo, então no momento do `handleConfirm`
 * `location` já pode estar em `/login`. Capturamos a rota original via
 * `useRef` no instante em que `sessionExpired` vira `true`.
 */
export function SessionExpiredModal() {
  const sessionExpired = useSessionStore((s) => s.sessionExpired);
  const setSessionExpired = useSessionStore((s) => s.setSessionExpired);
  const navigate = useNavigate();
  const location = useLocation();
  const lastAuthedRouteRef = useRef<string>('');

  // Mantém memorizada a última rota não-/login enquanto o modal está fechado;
  // ao abrir, congelamos esse valor como rota de retorno.
  useEffect(() => {
    if (!sessionExpired) {
      const current = `${location.pathname}${location.search}`;
      if (!current.startsWith('/login')) {
        lastAuthedRouteRef.current = current;
      }
    }
  }, [sessionExpired, location.pathname, location.search]);

  function close(): void {
    setSessionExpired(false);
  }

  function handleConfirm(): void {
    const next = lastAuthedRouteRef.current;
    // Não pendurar `?next=/login` recursivamente nem `?next=/` (default).
    const shouldKeepNext = next.length > 0 && next !== '/' && !next.startsWith('/login');
    const target = shouldKeepNext ? `/login?next=${encodeURIComponent(next)}` : '/login';
    close();
    navigate(target, { replace: true });
  }

  function handleCancel(): void {
    close();
    navigate('/login', { replace: true });
  }

  return (
    <ConfirmModal
      open={sessionExpired}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      title={ptBR.sessionExpired.title}
      description={ptBR.sessionExpired.body}
      confirmLabel={ptBR.sessionExpired.confirm}
      cancelLabel={ptBR.sessionExpired.cancel}
      variant="default"
      onConfirm={handleConfirm}
    />
  );
}
